/* global HSCore */

import React, { useContext, useEffect, useRef, useState } from "react";
import LanguageContext from "../utils/Contexts/LanguageContext";
import Info from "../components/svg/InfoIcon";
import { Loader } from "../components/Common";
import Constant from "../components/Constants";
import UserContext from "../utils/Contexts/UserContext";
import toast from "react-hot-toast";
import axiosInstance from "../utils/Requests/axios";
import _ from "lodash";

const MonitorFilterModal = ({
  setData,
  tempFilters,
  setTempFilters,
  fetchData,
  handleClear,
  closeModal,
  isSubmit,
}) => {
  const { t } = useContext(LanguageContext);
  const [selectedDonorOptions, setSelectedDonorOptions] = useState([]);
  const [selectedTargetOptions, setSelectedTargetOptions] = useState([]);
  const { statusOptions, relOptions } = Constant();
  const { userData } = useContext(UserContext);
  const isDisable = useRef(true);
  const [loadingStates, setLoadingStates] = useState({
    submit: false,
    cancel: false,
  });

  const handleChange = (e, type = "text") => {
    const { name, value, checked } = e.target;

    setTempFilters((prevFilters) =>
      prevFilters.map((filter) =>
        filter.key === name
          ? {
              ...filter,
              value:
                type === "checkbox"
                  ? checked
                    ? 1
                    : null
                  : value === "null"
                  ? null
                  : isNaN(value)
                  ? value
                  : parseInt(value),
            }
          : filter
      )
    );
    isDisable.current = false;
  };

  useEffect(() => {
    const allFiltersEmpty = tempFilters.every((filter) => filter?.value === "");
    isDisable.current = allFiltersEmpty;
  }, [tempFilters]);

  const applyFilter = async () => {
    isSubmit.current = true;
    try {
      setData((prevState) => ({
        ...prevState,
        lenght: 15,
        search: {
          ...prevState.search,
          filters: _.cloneDeep(tempFilters),
        },
      }));

      setLoadingStates({ submit: true, cancel: false });
      await new Promise((resolve) => setTimeout(resolve, 0));
      await fetchData();

      setTimeout(() => {
        setLoadingStates({ submit: false, cancel: false });
        closeModal();
      }, 300);
    } catch (error) {
      console.error("Error applying filter:", error);
      setLoadingStates({ submit: false, cancel: false });
    }
  };

  const clearFilters = async () => {
    try {
      setLoadingStates({ submit: false, cancel: true });
      clearTomSelectOptions("donor");
      clearTomSelectOptions("target");
      await handleClear();
      setTimeout(() => {
        setLoadingStates({ submit: false, cancel: false });
        closeModal();
      }, 300);
    } catch (error) {
      console.error("Error applying filter:", error);
      setLoadingStates({ submit: false, cancel: false });
    }
  };

  const hsInitRef = useRef(false);
  useEffect(() => {
    if (!hsInitRef.current) {
      // Initialize both Tom Selects
      HSCore.components.HSTomSelect.init(".js-select-donor"); // For the donor select
      HSCore.components.HSTomSelect.init(".js-select-target"); // For the target select

      // Set ref to true to prevent re-initialization
      hsInitRef.current = true;

      // Handle input events on the donor select input
      const donorInputEl = document.querySelector(
        ".js-select-donor .dropdown-input"
      );
      // Handle input events on the target select input
      const targetInputEl = document.querySelector(
        ".js-select-target .dropdown-input"
      );
      if (donorInputEl) {
        donorInputEl.addEventListener("input", async (e) => {
          const inputValue = e.target.value;
          const payload = {
            draw: 1,
            columns: [{ data: "created" }],
            order: [{ column: 0, dir: "desc" }],
            start: 0,
            length: 15,
            search: {
              value: inputValue,
              filters: [],
            },
          };

          try {
            const url = `users/${userData?.uuid}/links/search`;

            const response = await axiosInstance.post(url, payload);
            const results = response?.data?.data ?? [];
            // Update state for donor options
            setSelectedDonorOptions(results);
          } catch (error) {
            console.error("API error fetching donor data:", error);
            toast.error(t("There was an error fetching donor data"));
          }
        });
      }

      if (targetInputEl) {
        targetInputEl.addEventListener("input", async (e) => {
          const inputValue = e.target.value;

          const payload = {
            draw: 1,
            columns: [{ data: "created" }],
            order: [{ column: 0, dir: "desc" }],
            start: 0,
            length: 15,
            search: {
              value: inputValue,
              filters: [],
            },
          };

          try {
            const url = `users/${userData?.uuid}/links/search`;

            const response = await axiosInstance.post(url, payload);
            const results = response?.data?.data ?? [];
            setSelectedTargetOptions(results);
          } catch (error) {
            console.error("API error fetching target data:", error);
            toast.error(t("There was an error fetching target data"));
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDonorSelectChange = (event) => {
    const { name } = event.target;
    const options = Array.from(event.target.selectedOptions, (option) => ({
      value: option.value,
      label: option.label,
    }));

    setTempFilters((prevState) =>
      prevState.map((filter) =>
        filter.key === name
          ? {
              ...filter,
              value: options[0]?.value, // or handle multiple if needed
            }
          : filter
      )
    );
    isDisable.current = false;
  };

  const setTomMultiSelectValue = (identifier) => {
    const select = document.getElementsByClassName(`js-select-${identifier}`);
    if (select.length) {
      const control = select?.[0].tomselect;
      if (control) {
        if (identifier === "donor") {
          // Clear previous options
          control.clearOptions();

          // Add new options dynamically with validation
          selectedDonorOptions.forEach((domain) => {
            const value = domain?.uuid;
            const label = domain?.target?.page?.title || "Unknown Donor";

            if (value && label) {
              control.addOption({ value: value, text: label });
            } else {
              console.warn("Invalid data for domain:", domain);
            }
          });
          control.refreshOptions(); // Force refresh of options
        }
        if (identifier === "target") {
          // Clear previous options
          control.clearOptions();

          // Add new options dynamically with validation
          selectedTargetOptions.forEach((domain) => {
            const value = domain?.uuid;
            const label = domain?.target?.page?.title || "Unknown Donor";

            if (value && label) {
              control.addOption({ value: value, text: label });
            } else {
              console.warn("Invalid data for domain:", domain);
            }
          });
          control.refreshOptions(); // Force refresh of options
        }
      }
    }
  };

  useEffect(() => {
    setTomMultiSelectValue("donor");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDonorOptions]);

  useEffect(() => {
    setTomMultiSelectValue("target");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTargetOptions]);

  useEffect(() => {
    if (isSubmit.current === false) {
      const offcanvasEl = document.getElementById("monitorfilter");

      const handleHidden = () => {
        closeModal();
      };

      if (offcanvasEl) {
        offcanvasEl.addEventListener("hidden.bs.offcanvas", handleHidden);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmit]);

  const clearTomSelectOptions = (identifier) => {
    const select = document.getElementsByClassName(`js-select-${identifier}`);
    if (select.length) {
      const control = select[0].tomselect;
      if (control) {
        control.clear(); // Clear selected value
        control.clearOptions(); // Remove all dropdown options
      }
    }
  };

  return (
    <div>
      <div
        class="offcanvas offcanvas-end"
        tabindex="-1"
        id="monitorfilter"
        aria-labelledby="monitorfilter"
      >
        <div class="offcanvas-header monitor-filter-header">
          <h5
            id="monitorfilter"
            style={{ fontSize: "18px", fontWeight: "600" }}
          >
            {t("Filters")}
          </h5>
        </div>
        <div class="offcanvas-body monitor_filter_body">
          <form className="monitor_filter_form">
            <div class="mb-3">
              <label
                class="form-label monitor_label"
                htmlFor="exampleFormControlInput1"
              >
                {t("DONOR")} <Info style={{ marginTop: "-5px" }} />
              </label>
              <div className="tom-select-custom">
                <select
                  className="js-select-donor form-select"
                  autocomplete="off"
                  data-hs-tom-select-options={JSON?.stringify({
                    placeholder: t("Select Donor"),
                    searchInDropdown: true,
                    hideSearch: false,
                  })}
                  name="DONOR_UUID"
                  value={
                    tempFilters?.find((filter) => filter.key === "DONOR_UUID")
                      ?.value || ""
                  }
                  onChange={(e) => handleDonorSelectChange(e)}
                ></select>
              </div>
            </div>
            <div class="mb-3">
              <div class="form-check form-switch mb-4">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="formSwitch2"
                  name="DONOR_INDEXED"
                  onChange={(e) => handleChange(e, "checkbox")}
                  checked={
                    tempFilters?.find(
                      (filter) => filter.key === "DONOR_INDEXED"
                    )?.value || ""
                  }
                />
                <label
                  class="form-check-label monitor_sub_label"
                  htmlFor="formSwitch2"
                >
                  {t("Indexed")} <Info style={{ marginTop: "-5px" }} />
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label
                class="form-label monitor_label"
                htmlFor="exampleFormControlInput1"
              >
                {t("TARGET")} <Info style={{ marginTop: "-5px" }} />
              </label>
              <div className="tom-select-custom">
                <select
                  className="js-select-target form-select"
                  autocomplete="off"
                  data-hs-tom-select-options={JSON?.stringify({
                    placeholder: t("Select Target"),
                    searchInDropdown: true,
                    hideSearch: false,
                  })}
                  onChange={(e) => handleDonorSelectChange(e)}
                  name="TARGET_UUID"
                  value={
                    tempFilters?.find((filter) => filter.key === "TARGET_UUID")
                      ?.value || ""
                  }
                ></select>
              </div>
            </div>
            <div class="mb-3">
              <div class="form-check form-switch mb-4">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="formSwitch2"
                  name="TARGET_INDEXED"
                  onChange={(e) => handleChange(e, "checkbox")}
                  checked={
                    tempFilters?.find(
                      (filter) => filter.key === "TARGET_INDEXED"
                    )?.value || ""
                  }
                />
                <label
                  class="form-check-label monitor_sub_label"
                  htmlFor="formSwitch2"
                >
                  {t("Indexed")} <Info style={{ marginTop: "-5px" }} />
                </label>
              </div>
            </div>

            <hr />
            <div class="row mb-3">
              <div class="col-6">
                <label
                  class="form-label monitor_label"
                  htmlFor="exampleFormControlInput1"
                >
                  {t("STATUS")} <Info style={{ marginTop: "-5px" }} />
                </label>
                <select
                  class="form-select monitor_filter_sub_select"
                  className="form-select monitor_filter_sub_select"
                  name="STATUS"
                  onChange={(e) => handleChange(e)}
                  value={
                    tempFilters?.find((filter) => filter.key === "STATUS")
                      ?.value || ""
                  }
                >
                  <option value="null">{t("Any")}</option>
                  {statusOptions?.map((opt) => {
                    return <option value={opt?.value}>{opt?.label}</option>;
                  })}
                </select>
              </div>
              <div class="col-6">
                <label
                  class="form-label monitor_label"
                  htmlFor="exampleFormControlInput1"
                >
                  {t("REL")} <Info style={{ marginTop: "-5px" }} />
                </label>
                <select
                  class="form-select monitor_filter_sub_select"
                  name="REL"
                  onChange={(e) => handleChange(e)}
                  value={
                    tempFilters?.find((filter) => filter.key === "REL")
                      ?.value || ""
                  }
                >
                  <option value="null">{t("Any")}</option>
                  {relOptions?.map((opt) => {
                    return <option value={opt?.value}>{opt?.label}</option>;
                  })}
                </select>
              </div>
            </div>
            <label
              class="form-label monitor_label"
              htmlFor="exampleFormControlInput1"
            >
              {t("TYPE")} <Info style={{ marginTop: "-5px" }} />
            </label>
            <select
              class="form-select monitor_filter_select"
              name="TYPE"
              onChange={(e) => handleChange(e)}
              value={
                tempFilters?.find((filter) => filter.key === "TYPE")?.value ||
                ""
              }
            >
              <option value="null">{t("Any")}</option>
              <option value="1">{t("Text")}</option>
              <option value="2">{t("Image")}</option>
            </select>
          </form>
        </div>
        <div class="offcanvas-footer p-3">
          <div class="d-flex justify-content-center gap-2">
            <button
              class="btn btn-outline-secondary filter_btn"
              type="button"
              style={{
                border: "1px solid var(--border)",
                cursor: isDisable?.current ? "not-allowed" : "pointer",
              }}
              onClick={clearFilters}
              disabled={isDisable.current || false}
            >
              {loadingStates?.cancel ? <Loader /> : t("Clear all filters")}
            </button>
            <button
              class="btn filter_btn"
              type="button"
              onClick={applyFilter}
              style={{
                backgroundColor: "#387CFF",
                border: "none",
                color: "white",
                cursor: isDisable?.current ? "not-allowed" : "pointer",
              }}
              disabled={isDisable.current || false}
            >
              {loadingStates?.submit ? <Loader /> : t("Apply")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitorFilterModal;
